'use client';

import { useState, useEffect } from 'react';
import TaskCard from './TaskCard';
import ExpenseItem from './ExpenseItem';
import UpdateFeedItem from './UpdateFeedItem';
import FamilyMemberCard from './FamilyMemberCard';
import ContactCard from './ContactCard';
import Icon from '@/components/ui/AppIcon';
import AlertNotificationBadge from '@/components/common/AlertNotificationBadge';
import FamilyActivityIndicator from '@/components/common/FamilyActivityIndicator';
import QuickActionButton from '@/components/common/QuickActionButton';

interface Task {
  id: string;
  title: string;
  description: string;
  assignee: {
    name: string;
    avatar: string;
    alt: string;
  };
  dueDate: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'todo' | 'in-progress' | 'completed';
  category: string;
}

interface Expense {
  id: string;
  description: string;
  amount: number;
  paidBy: {
    name: string;
    avatar: string;
    alt: string;
  };
  date: string;
  category: string;
  receipt?: string;
  receiptAlt?: string;
  splitWith: string[];
  settled: boolean;
}

interface Update {
  id: string;
  type: 'task' | 'expense' | 'comment' | 'system';
  author: {
    name: string;
    avatar: string;
    alt: string;
  };
  content: string;
  timestamp: string;
  comments?: Array<{
    id: string;
    author: {
      name: string;
      avatar: string;
      alt: string;
    };
    content: string;
    timestamp: string;
  }>;
}

interface FamilyMember {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar: string;
  alt: string;
  role: 'admin' | 'editor' | 'viewer';
  isOnline: boolean;
  lastActive: string;
}

interface Contact {
  id: string;
  name: string;
  type: string;
  phone: string;
  email?: string;
  address?: string;
  notes?: string;
}

const FamilyCoordinationInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState<'tasks' | 'expenses' | 'updates' | 'members' | 'contacts'>('tasks');
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [showNewExpenseModal, setShowNewExpenseModal] = useState(false);
  const [showInviteMemberModal, setShowInviteMemberModal] = useState(false);
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [taskFilter, setTaskFilter] = useState<'all' | 'todo' | 'in-progress' | 'completed'>('all');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const [tasks, setTasks] = useState<Task[]>([
  {
    id: '1',
    title: 'Schedule Mom\'s cardiology appointment',
    description: 'Call Dr. Martinez office to schedule follow-up for blood pressure monitoring',
    assignee: {
      name: 'Sarah Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d92ac120-1763293804988.png",
      alt: 'Professional headshot of Asian woman with long dark hair in white blouse smiling at camera'
    },
    dueDate: '02/16/2026',
    priority: 'high',
    status: 'todo',
    category: 'Medical'
  },
  {
    id: '2',
    title: 'Pick up prescriptions',
    description: 'Collect refills for Lisinopril and Metformin from CVS Pharmacy',
    assignee: {
      name: 'Michael Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b96df5c7-1763298849215.png",
      alt: 'Professional headshot of Asian man with short black hair in navy suit smiling confidently'
    },
    dueDate: '02/15/2026',
    priority: 'urgent',
    status: 'in-progress',
    category: 'Medication'
  },
  {
    id: '3',
    title: 'Review Medicare Advantage plans',
    description: 'Compare 2026 plan options and benefits for annual enrollment period',
    assignee: {
      name: 'Jennifer Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_16fb6600f-1763295222357.png",
      alt: 'Professional headshot of woman with shoulder-length brown hair in gray blazer smiling warmly'
    },
    dueDate: '02/20/2026',
    priority: 'medium',
    status: 'todo',
    category: 'Insurance'
  },
  {
    id: '4',
    title: 'Install grab bars in bathroom',
    description: 'Arrange handyman to install safety grab bars near toilet and shower',
    assignee: {
      name: 'David Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13b6f8537-1763295903462.png",
      alt: 'Professional headshot of man with short brown hair and beard in casual blue shirt outdoors'
    },
    dueDate: '02/18/2026',
    priority: 'high',
    status: 'todo',
    category: 'Home Safety'
  },
  {
    id: '5',
    title: 'Update emergency contact list',
    description: 'Add new neighbors and update doctor contact information',
    assignee: {
      name: 'Sarah Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d92ac120-1763293804988.png",
      alt: 'Professional headshot of Asian woman with long dark hair in white blouse smiling at camera'
    },
    dueDate: '02/14/2026',
    priority: 'low',
    status: 'completed',
    category: 'Planning'
  }]
  );

  const [expenses, setExpenses] = useState<Expense[]>([
  {
    id: '1',
    description: 'Monthly prescription refills',
    amount: 127.50,
    paidBy: {
      name: 'Sarah Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d92ac120-1763293804988.png",
      alt: 'Professional headshot of Asian woman with long dark hair in white blouse smiling at camera'
    },
    date: '02/01/2026',
    category: 'Medication',
    receipt: "https://img.rocket.new/generatedImages/rocket_gen_img_1cc00ccbc-1767246374888.png",
    receiptAlt: 'Pharmacy receipt showing itemized prescription costs and payment details',
    splitWith: ['Michael Chen', 'Jennifer Chen'],
    settled: false
  },
  {
    id: '2',
    description: 'Home health aide services',
    amount: 480.00,
    paidBy: {
      name: 'Michael Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b96df5c7-1763298849215.png",
      alt: 'Professional headshot of Asian man with short black hair in navy suit smiling confidently'
    },
    date: '02/05/2026',
    category: 'Care Services',
    splitWith: ['Sarah Chen', 'Jennifer Chen', 'David Chen'],
    settled: true
  },
  {
    id: '3',
    description: 'Medical equipment rental',
    amount: 85.00,
    paidBy: {
      name: 'Jennifer Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_16fb6600f-1763295222357.png",
      alt: 'Professional headshot of woman with shoulder-length brown hair in gray blazer smiling warmly'
    },
    date: '02/10/2026',
    category: 'Medical Equipment',
    receipt: "https://img.rocket.new/generatedImages/rocket_gen_img_1eaf6cc47-1771053041918.png",
    receiptAlt: 'Medical equipment rental invoice showing walker and wheelchair costs',
    splitWith: ['Sarah Chen', 'Michael Chen'],
    settled: false
  },
  {
    id: '4',
    description: 'Grocery delivery service',
    amount: 156.32,
    paidBy: {
      name: 'David Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13b6f8537-1763295903462.png",
      alt: 'Professional headshot of man with short brown hair and beard in casual blue shirt outdoors'
    },
    date: '02/12/2026',
    category: 'Daily Living',
    splitWith: ['Sarah Chen'],
    settled: true
  }]
  );

  const [updates, setUpdates] = useState<Update[]>([
  {
    id: '1',
    type: 'task',
    author: {
      name: 'Sarah Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d92ac120-1763293804988.png",
      alt: 'Professional headshot of Asian woman with long dark hair in white blouse smiling at camera'
    },
    content: 'Completed updating emergency contact list with new neighbor information',
    timestamp: '2 hours ago',
    comments: [
    {
      id: 'c1',
      author: {
        name: 'Michael Chen',
        avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b96df5c7-1763298849215.png",
        alt: 'Professional headshot of Asian man with short black hair in navy suit smiling confidently'
      },
      content: 'Great! Did you include their phone numbers?',
      timestamp: '1 hour ago'
    },
    {
      id: 'c2',
      author: {
        name: 'Sarah Chen',
        avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d92ac120-1763293804988.png",
        alt: 'Professional headshot of Asian woman with long dark hair in white blouse smiling at camera'
      },
      content: 'Yes, both cell and home numbers are included',
      timestamp: '45 minutes ago'
    }]

  },
  {
    id: '2',
    type: 'expense',
    author: {
      name: 'Michael Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b96df5c7-1763298849215.png",
      alt: 'Professional headshot of Asian man with short black hair in navy suit smiling confidently'
    },
    content: 'Added expense for home health aide services ($480.00)',
    timestamp: '5 hours ago',
    comments: []
  },
  {
    id: '3',
    type: 'system',
    author: {
      name: 'System',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1752cb0e7-1766550747539.png",
      alt: 'Generic system avatar with blue gradient background'
    },
    content: 'Reminder: Mom\'s cardiology appointment is scheduled for tomorrow at 10:00 AM',
    timestamp: '1 day ago',
    comments: []
  },
  {
    id: '4',
    type: 'comment',
    author: {
      name: 'Jennifer Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_16fb6600f-1763295222357.png",
      alt: 'Professional headshot of woman with shoulder-length brown hair in gray blazer smiling warmly'
    },
    content: 'I spoke with Dr. Martinez office - they confirmed the appointment and mentioned bringing the medication list',
    timestamp: '1 day ago',
    comments: [
    {
      id: 'c3',
      author: {
        name: 'Sarah Chen',
        avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d92ac120-1763293804988.png",
        alt: 'Professional headshot of Asian woman with long dark hair in white blouse smiling at camera'
      },
      content: 'Perfect, I\'ll print the updated list tonight',
      timestamp: '1 day ago'
    }]

  }]
  );

  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([
  {
    id: '1',
    name: 'Sarah Chen',
    email: 'sarah.chen@email.com',
    phone: '(555) 123-4567',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d92ac120-1763293804988.png",
    alt: 'Professional headshot of Asian woman with long dark hair in white blouse smiling at camera',
    role: 'admin',
    isOnline: true,
    lastActive: 'now'
  },
  {
    id: '2',
    name: 'Michael Chen',
    email: 'michael.chen@email.com',
    phone: '(555) 234-5678',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b96df5c7-1763298849215.png",
    alt: 'Professional headshot of Asian man with short black hair in navy suit smiling confidently',
    role: 'admin',
    isOnline: true,
    lastActive: 'now'
  },
  {
    id: '3',
    name: 'Jennifer Chen',
    email: 'jennifer.chen@email.com',
    phone: '(555) 345-6789',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_16fb6600f-1763295222357.png",
    alt: 'Professional headshot of woman with shoulder-length brown hair in gray blazer smiling warmly',
    role: 'editor',
    isOnline: false,
    lastActive: '2 hours ago'
  },
  {
    id: '4',
    name: 'David Chen',
    email: 'david.chen@email.com',
    phone: '(555) 456-7890',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13b6f8537-1763295903462.png",
    alt: 'Professional headshot of man with short brown hair and beard in casual blue shirt outdoors',
    role: 'viewer',
    isOnline: false,
    lastActive: '1 day ago'
  },
  {
    id: '5',
    name: 'Dr. Robert Chen',
    email: 'dr.robert.chen@medical.com',
    phone: '(555) 567-8901',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13e452f85-1763294640182.png",
    alt: 'Professional photo of Caucasian man with short brown hair in business casual attire',
    role: 'viewer',
    isOnline: false,
    lastActive: '3 hours ago'
  }]
  );

  const [contacts, setContacts] = useState<Contact[]>([
  {
    id: '1',
    name: 'Dr. Maria Martinez',
    type: 'Primary Care Physician',
    phone: '(555) 111-2222',
    email: 'dr.martinez@healthcenter.com',
    address: '123 Medical Plaza, Suite 200, Springfield, IL 62701',
    notes: 'Preferred doctor for 15 years, speaks Spanish'
  },
  {
    id: '2',
    name: 'CVS Pharmacy',
    type: 'Pharmacy',
    phone: '(555) 333-4444',
    email: 'prescriptions@cvs.com',
    address: '456 Main Street, Springfield, IL 62702',
    notes: 'Automatic refill program enrolled'
  },
  {
    id: '3',
    name: 'Medicare Helpline',
    type: 'Insurance Support',
    phone: '1-800-MEDICARE',
    email: 'support@medicare.gov',
    notes: 'Available 24/7 for coverage questions'
  },
  {
    id: '4',
    name: 'Emergency Services',
    type: 'Emergency Contact',
    phone: '911',
    notes: 'For immediate medical emergencies only'
  },
  {
    id: '5',
    name: 'Home Health Care Plus',
    type: 'Care Services',
    phone: '(555) 777-8888',
    email: 'scheduling@homehealthplus.com',
    address: '789 Care Avenue, Springfield, IL 62703',
    notes: 'Weekly visits on Tuesdays and Fridays'
  }]
  );

  if (!isHydrated) {
    return (
      <div className="flex-1 p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="h-8 w-64 bg-muted rounded animate-pulse mb-6"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {[1, 2, 3].map((i) =>
              <div key={i} className="h-32 bg-muted rounded-lg animate-pulse"></div>
              )}
            </div>
            <div className="space-y-4">
              {[1, 2].map((i) =>
              <div key={i} className="h-48 bg-muted rounded-lg animate-pulse"></div>
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  const handleTaskStatusChange = (taskId: string, newStatus: 'todo' | 'in-progress' | 'completed') => {
    setTasks(tasks.map((task) =>
    task.id === taskId ? { ...task, status: newStatus } : task
    ));
  };

  const handleExpenseToggleSettle = (expenseId: string) => {
    setExpenses(expenses.map((expense) =>
    expense.id === expenseId ? { ...expense, settled: !expense.settled } : expense
    ));
  };

  const handleAddComment = (updateId: string, comment: string) => {
    setUpdates(updates.map((update) => {
      if (update.id === updateId) {
        const newComment = {
          id: `c${Date.now()}`,
          author: familyMembers[0],
          content: comment,
          timestamp: 'Just now'
        };
        return {
          ...update,
          comments: [...(update.comments || []), newComment]
        };
      }
      return update;
    }));
  };

  const handleEditMemberRole = (memberId: string, newRole: 'admin' | 'editor' | 'viewer') => {
    setFamilyMembers(familyMembers.map((member) =>
    member.id === memberId ? { ...member, role: newRole } : member
    ));
  };

  const filteredTasks = taskFilter === 'all' ?
  tasks :
  tasks.filter((task) => task.status === taskFilter);

  const todoTasks = tasks.filter((t) => t.status === 'todo');
  const inProgressTasks = tasks.filter((t) => t.status === 'in-progress');
  const completedTasks = tasks.filter((t) => t.status === 'completed');

  const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  const settledExpenses = expenses.filter((exp) => exp.settled).reduce((sum, exp) => sum + exp.amount, 0);
  const pendingExpenses = totalExpenses - settledExpenses;

  const quickActions = [
  {
    id: 'new-task',
    label: 'Create Task',
    icon: 'PlusCircleIcon',
    onClick: () => setShowNewTaskModal(true)
  },
  {
    id: 'new-expense',
    label: 'Add Expense',
    icon: 'CurrencyDollarIcon',
    onClick: () => setShowNewExpenseModal(true)
  },
  {
    id: 'invite-member',
    label: 'Invite Member',
    icon: 'UserPlusIcon',
    onClick: () => setShowInviteMemberModal(true)
  },
  {
    id: 'new-contact',
    label: 'Add Contact',
    icon: 'PhoneIcon',
    onClick: () => setShowNewContactModal(true)
  }];


  return (
    <>
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 lg:p-8">
          <div className="w-full">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
              <div>
                <h1 className="text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-2">
                  Family Coordination Hub
                </h1>
                <p className="text-sm lg:text-base text-muted-foreground">
                  Collaborate with family members on caregiving tasks and expenses
                </p>
              </div>
              <div className="flex items-center gap-3">
                <FamilyActivityIndicator
                  members={familyMembers}
                  showDetails={true} />
                
                <button
                  onClick={() => setShowInviteMemberModal(true)}
                  className="flex items-center gap-2 px-3 lg:px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth text-sm lg:text-base">
                  
                  <Icon name="UserPlusIcon" size={20} />
                  <span className="font-caption font-medium hidden sm:inline">Invite Member</span>
                  <span className="font-caption font-medium sm:hidden">Invite</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-8">
              <div className="glass p-3 lg:p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs lg:text-sm font-caption text-muted-foreground">Active Tasks</span>
                  <Icon name="ClipboardDocumentCheckIcon" size={18} className="text-primary" />
                </div>
                <p className="text-xl lg:text-2xl font-heading font-semibold text-foreground">
                  {todoTasks.length + inProgressTasks.length}
                </p>
              </div>
              <div className="glass p-3 lg:p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs lg:text-sm font-caption text-muted-foreground">Pending Expenses</span>
                  <Icon name="CurrencyDollarIcon" size={18} className="text-accent" />
                </div>
                <p className="text-xl lg:text-2xl font-heading font-semibold text-foreground break-all">
                  ${pendingExpenses.toFixed(2)}
                </p>
              </div>
              <div className="glass p-3 lg:p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs lg:text-sm font-caption text-muted-foreground">Family Members</span>
                  <Icon name="UsersIcon" size={18} className="text-success" />
                </div>
                <p className="text-xl lg:text-2xl font-heading font-semibold text-foreground">
                  {familyMembers.length}
                </p>
              </div>
              <div className="glass p-3 lg:p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs lg:text-sm font-caption text-muted-foreground">Contacts</span>
                  <Icon name="PhoneIcon" size={18} className="text-secondary" />
                </div>
                <p className="text-xl lg:text-2xl font-heading font-semibold text-foreground">
                  {contacts.length}
                </p>
              </div>
            </div>

            <div className="glass mb-6">
              <div className="flex items-center border-b border-sage-200/30 overflow-x-auto scrollbar-hide">
                <button
                  onClick={() => setActiveTab('tasks')}
                  className={`flex items-center gap-2 px-3 md:px-6 py-3 lg:py-4 font-caption font-medium transition-smooth border-b-2 whitespace-nowrap text-sm lg:text-base ${
                  activeTab === 'tasks' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`
                  }>                  
                  <Icon name="ClipboardDocumentCheckIcon" size={18} />
                  <span className="hidden sm:inline">Tasks</span>
                  <span className="sm:hidden">Tasks</span>
                  <AlertNotificationBadge count={todoTasks.length + inProgressTasks.length} priority="medium" />
                </button>
                <button
                  onClick={() => setActiveTab('expenses')}
                  className={`flex items-center gap-2 px-3 md:px-6 py-3 lg:py-4 font-caption font-medium transition-smooth border-b-2 whitespace-nowrap text-sm lg:text-base ${
                  activeTab === 'expenses' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`
                  }>
                  
                  <Icon name="CurrencyDollarIcon" size={18} />
                  <span className="hidden sm:inline">Expenses</span>
                  <span className="sm:hidden">Expenses</span>
                  <AlertNotificationBadge count={expenses.filter((e) => !e.settled).length} priority="low" />
                </button>
                <button
                  onClick={() => setActiveTab('updates')}
                  className={`flex items-center gap-2 px-3 md:px-6 py-3 lg:py-4 font-caption font-medium transition-smooth border-b-2 whitespace-nowrap text-sm lg:text-base ${
                  activeTab === 'updates' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`
                  }>
                  
                  <Icon name="ChatBubbleLeftRightIcon" size={18} />
                  <span className="hidden sm:inline">Updates</span>
                  <span className="sm:hidden">Updates</span>
                </button>
                <button
                  onClick={() => setActiveTab('members')}
                  className={`flex items-center gap-2 px-3 md:px-6 py-3 lg:py-4 font-caption font-medium transition-smooth border-b-2 whitespace-nowrap text-sm lg:text-base ${
                  activeTab === 'members' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`
                  }>
                  
                  <Icon name="UsersIcon" size={18} />
                  <span className="hidden sm:inline">Members</span>
                  <span className="sm:hidden">Members</span>
                </button>
                <button
                  onClick={() => setActiveTab('contacts')}
                  className={`flex items-center gap-2 px-3 md:px-6 py-3 lg:py-4 font-caption font-medium transition-smooth border-b-2 whitespace-nowrap text-sm lg:text-base ${
                  activeTab === 'contacts' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`
                  }>
                  
                  <Icon name="PhoneIcon" size={18} />
                  <span className="hidden sm:inline">Contacts</span>
                  <span className="sm:hidden">Contacts</span>
                </button>
              </div>

              <div className="p-3 md:p-4 lg:p-6">
                {activeTab === 'tasks' &&
                <div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                      <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0 scrollbar-hide">
                        <button
                        onClick={() => setTaskFilter('all')}
                        className={`px-4 py-2 rounded-lg font-caption font-medium transition-smooth whitespace-nowrap ${
                        taskFilter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`
                        }>
                        
                          All ({tasks.length})
                        </button>
                        <button
                        onClick={() => setTaskFilter('todo')}
                        className={`px-4 py-2 rounded-lg font-caption font-medium transition-smooth whitespace-nowrap ${
                        taskFilter === 'todo' ?
                        'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`
                        }>
                        
                          To Do ({todoTasks.length})
                        </button>
                        <button
                        onClick={() => setTaskFilter('in-progress')}
                        className={`px-4 py-2 rounded-lg font-caption font-medium transition-smooth whitespace-nowrap ${
                        taskFilter === 'in-progress' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`
                        }>
                        
                          In Progress ({inProgressTasks.length})
                        </button>
                        <button
                        onClick={() => setTaskFilter('completed')}
                        className={`px-4 py-2 rounded-lg font-caption font-medium transition-smooth whitespace-nowrap ${
                        taskFilter === 'completed' ?
                        'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`
                        }>
                        
                          Completed ({completedTasks.length})
                        </button>
                      </div>
                      <button
                      onClick={() => setShowNewTaskModal(true)}
                      className="flex items-center gap-2 px-3 lg:px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth whitespace-nowrap text-sm lg:text-base">
                      
                        <Icon name="PlusIcon" size={20} />
                        New Task
                      </button>
                    </div>

                    <div className="hidden lg:grid lg:grid-cols-3 gap-4 lg:gap-6">
                      <div>
                        <h3 className="font-caption font-medium text-foreground mb-4 flex items-center gap-2">
                          <Icon name="CircleStackIcon" size={20} className="text-muted-foreground" />
                          To Do ({todoTasks.length})
                        </h3>
                        <div className="space-y-4">
                          {todoTasks.map((task) =>
                        <TaskCard
                          key={task.id}
                          task={task}
                          onStatusChange={handleTaskStatusChange}
                          onEdit={(id) => console.log('Edit task:', id)}
                          onDelete={(id) => setTasks(tasks.filter((t) => t.id !== id))} />

                        )}
                        </div>
                      </div>
                      <div>
                        <h3 className="font-caption font-medium text-foreground mb-4 flex items-center gap-2">
                          <Icon name="ClockIcon" size={20} className="text-accent" />
                          In Progress ({inProgressTasks.length})
                        </h3>
                        <div className="space-y-4">
                          {inProgressTasks.map((task) =>
                        <TaskCard
                          key={task.id}
                          task={task}
                          onStatusChange={handleTaskStatusChange}
                          onEdit={(id) => console.log('Edit task:', id)}
                          onDelete={(id) => setTasks(tasks.filter((t) => t.id !== id))} />

                        )}
                        </div>
                      </div>
                      <div>
                        <h3 className="font-caption font-medium text-foreground mb-4 flex items-center gap-2">
                          <Icon name="CheckCircleIcon" size={20} className="text-success" />
                          Completed ({completedTasks.length})
                        </h3>
                        <div className="space-y-4">
                          {completedTasks.map((task) =>
                        <TaskCard
                          key={task.id}
                          task={task}
                          onStatusChange={handleTaskStatusChange}
                          onEdit={(id) => console.log('Edit task:', id)}
                          onDelete={(id) => setTasks(tasks.filter((t) => t.id !== id))} />

                        )}
                        </div>
                      </div>
                    </div>

                    <div className="lg:hidden space-y-3 lg:space-y-4">
                      {filteredTasks.map((task) =>
                    <TaskCard
                      key={task.id}
                      task={task}
                      onStatusChange={handleTaskStatusChange}
                      onEdit={(id) => console.log('Edit task:', id)}
                      onDelete={(id) => setTasks(tasks.filter((t) => t.id !== id))} />

                    )}
                    </div>
                  </div>
                }

                {activeTab === 'expenses' &&
                <div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                      <div className="bg-sage-50 dark:bg-sage-900/20 rounded-lg p-3 lg:p-4 flex-1">
                        <div className="grid grid-cols-3 gap-3 lg:gap-4 text-center">
                          <div>
                            <p className="text-xs font-caption text-muted-foreground mb-1">Total</p>
                            <p className="text-lg font-heading font-semibold text-foreground">
                              ${totalExpenses.toFixed(2)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-caption text-muted-foreground mb-1">Settled</p>
                            <p className="text-lg font-heading font-semibold text-success">
                              ${settledExpenses.toFixed(2)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-caption text-muted-foreground mb-1">Pending</p>
                            <p className="text-lg font-heading font-semibold text-accent">
                              ${pendingExpenses.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                      <button
                      onClick={() => setShowNewExpenseModal(true)}
                      className="flex items-center gap-2 px-3 lg:px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth whitespace-nowrap text-sm lg:text-base">
                      
                        <Icon name="PlusIcon" size={20} />
                        Add Expense
                      </button>
                    </div>

                    <div className="space-y-3 lg:space-y-4">
                      {expenses.map((expense) =>
                    <ExpenseItem
                      key={expense.id}
                      expense={expense}
                      onEdit={(id) => console.log('Edit expense:', id)}
                      onDelete={(id) => setExpenses(expenses.filter((e) => e.id !== id))}
                      onToggleSettle={handleExpenseToggleSettle} />

                    )}
                    </div>
                  </div>
                }

                {activeTab === 'updates' &&
                <div className="space-y-3 lg:space-y-4">
                    {updates.map((update) =>
                  <UpdateFeedItem
                    key={update.id}
                    update={update}
                    onAddComment={handleAddComment} />

                  )}
                  </div>
                }

                {activeTab === 'members' &&
                <div>
                    <div className="flex justify-end mb-4 lg:mb-6">
                      <button
                      onClick={() => setShowInviteMemberModal(true)}
                      className="flex items-center gap-2 px-3 lg:px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth text-sm lg:text-base">
                      
                        <Icon name="UserPlusIcon" size={20} />
                        Invite Member
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4">
                      {familyMembers.map((member) =>
                    <FamilyMemberCard
                      key={member.id}
                      member={member}
                      onEditRole={handleEditMemberRole}
                      onRemove={(id) => setFamilyMembers(familyMembers.filter((m) => m.id !== id))}
                      isCurrentUser={member.id === '1'} />

                    )}
                    </div>
                  </div>
                }

                {activeTab === 'contacts' &&
                <div>
                    <div className="flex justify-end mb-4 lg:mb-6">
                      <button
                      onClick={() => setShowNewContactModal(true)}
                      className="flex items-center gap-2 px-3 lg:px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth text-sm lg:text-base">
                      
                        <Icon name="PlusIcon" size={20} />
                        Add Contact
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4">
                      {contacts.map((contact) =>
                    <ContactCard
                      key={contact.id}
                      contact={contact}
                      onEdit={(id) => console.log('Edit contact:', id)}
                      onDelete={(id) => setContacts(contacts.filter((c) => c.id !== id))}
                      onCall={(phone) => console.log('Call:', phone)}
                      onEmail={(email) => console.log('Email:', email)} />

                    )}
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>

      <QuickActionButton actions={quickActions} />

      {showNewTaskModal &&
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="glass rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-sage-200/30">
              <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground">
                Create New Task
              </h3>
              <button
              onClick={() => setShowNewTaskModal(false)}
              className="p-2 rounded-lg hover:bg-muted transition-smooth"
              aria-label="Close modal">
              
                <Icon name="XMarkIcon" size={20} className="text-muted-foreground" />
              </button>
            </div>
            <div className="p-4 md:p-6">
              <p className="text-sm text-muted-foreground text-center py-8">
                Task creation form would be implemented here
              </p>
            </div>
          </div>
        </div>
      }

      {showNewExpenseModal &&
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="glass rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-sage-200/30">
              <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground">
                Add New Expense
              </h3>
              <button
              onClick={() => setShowNewExpenseModal(false)}
              className="p-2 rounded-lg hover:bg-muted transition-smooth"
              aria-label="Close modal">
              
                <Icon name="XMarkIcon" size={20} className="text-muted-foreground" />
              </button>
            </div>
            <div className="p-4 md:p-6">
              <p className="text-sm text-muted-foreground text-center py-8">
                Expense creation form would be implemented here
              </p>
            </div>
          </div>
        </div>
      }

      {showInviteMemberModal &&
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="glass rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-sage-200/30">
              <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground">
                Invite Family Member
              </h3>
              <button
              onClick={() => setShowInviteMemberModal(false)}
              className="p-2 rounded-lg hover:bg-muted transition-smooth"
              aria-label="Close modal">
              
                <Icon name="XMarkIcon" size={20} className="text-muted-foreground" />
              </button>
            </div>
            <div className="p-4 md:p-6">
              <p className="text-sm text-muted-foreground text-center py-8">
                Member invitation form would be implemented here
              </p>
            </div>
          </div>
        </div>
      }

      {showNewContactModal &&
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="glass rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-sage-200/30">
              <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground">
                Add New Contact
              </h3>
              <button
              onClick={() => setShowNewContactModal(false)}
              className="p-2 rounded-lg hover:bg-muted transition-smooth"
              aria-label="Close modal">
              
                <Icon name="XMarkIcon" size={20} className="text-muted-foreground" />
              </button>
            </div>
            <div className="p-4 md:p-6">
              <p className="text-sm text-muted-foreground text-center py-8">
                Contact creation form would be implemented here
              </p>
            </div>
          </div>
        </div>
      }
    </>);

};

export default FamilyCoordinationInteractive;